import React, { useMemo } from 'react'
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { useEvent } from 'react-use-event-hook'
import { EditGating } from '@components/Web3/EditMembership/EditGating'
import { EditMembership } from '@components/Web3/EditMembership/EditMembership'
import { EditPricing } from '@components/Web3/EditMembership/EditPricing'
import { EditPricingTitle } from '@components/Web3/EditMembership/EditPricingTitle'
import { EditUsdcPricing } from '@components/Web3/EditMembership/EditUsdcPricing'
import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { FullPanelOverlay } from '@components/Web3/WalletLinkingPanel'
import { Button, FormRender, Icon, Paragraph, Stack, Text } from '@ui'
import { BigNumber } from 'ethers'
import { formatUnits } from 'hooks/useBalance'
import { useEntitlements } from 'hooks/useEntitlements'
import { useEnvironment } from 'hooks/useEnvironment'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { usePlatformMinMembershipPriceInEth } from 'hooks/usePlatformMinMembershipPriceInEth'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    BlockchainTransactionType,
    useIsTransactionPending,
    useMembershipInfo,
    useMembershipInfoWithPricingModule,
    useMembershipPrice,
    usePricingModules,
    useProtocolFee,
    useRoleDetails,
} from 'use-towns-client'
import { formatUnits as formatUnitsViem, zeroAddress } from 'viem'

import { editMembershipSchema, EditMembershipSchemaType } from './editMembershipSchema'
import { editMembershipSchemaUsdc, EditMembershipSchemaUsdcType } from './editMembershipSchemaUsdc'
import { EditMembershipSubmitButton } from './EditMembershipSubmitButton'
import { EditMembershipSubmitButtonUsdc } from './EditMembershipSubmitButtonUsdc'
import { useIsSpaceCurrentlyDynamic } from './hooks'
import { minterRoleId } from './rolePermissions.const'

export const EditMembershipSettingsPanel = React.memo(
    ({ onSuccess }: { onSuccess?: () => void }) => {
        const spaceIdFromPath = useSpaceIdFromPathname()
        const transactionIsPending = useIsTransactionPending(
            BlockchainTransactionType.EditSpaceMembership,
        )
        const onChainData = useLookupOnChainDataForThisForm({
            spaceId: spaceIdFromPath,
        })
        const { address: usdcAddress } = useUSDCMetadata()
        const { enableUsdc } = useFeatureFlags()

        const isUsdcSpace =
            onChainData.membershipInfo?.currency?.toLowerCase() === usdcAddress.toLowerCase()

        return (
            <>
                <Stack gap grow position="relative">
                    {onChainData.isLoadingSomeData ? (
                        <Stack grow centerContent role="status">
                            <ButtonSpinner />
                        </Stack>
                    ) : onChainData.someErrorOccurred ? (
                        <Paragraph>Failed to load membership info</Paragraph>
                    ) : !enableUsdc ? (
                        <EditMembershipForm
                            spaceId={spaceIdFromPath}
                            onChainData={onChainData}
                            onSuccess={onSuccess}
                        />
                    ) : enableUsdc && isUsdcSpace ? (
                        <EditMembershipFormUsdc
                            spaceId={spaceIdFromPath}
                            onChainData={onChainData}
                            onSuccess={onSuccess}
                        />
                    ) : (
                        <SwitchToUsdcNotice onSuccess={onSuccess} />
                    )}
                </Stack>
                {transactionIsPending && <FullPanelOverlay text="" withSpinner={false} />}
            </>
        )
    },
)

function SwitchToUsdcNotice(props: { onSuccess?: () => void }) {
    const { onSuccess } = props
    const { openPanel } = usePanelActions()
    const onUpgradeToUsdcClick = useEvent(() => {
        openPanel(CHANNEL_INFO_PARAMS.UPGRADE_TO_USDC)
        onSuccess?.()
    })

    return (
        <Stack gap="paragraph">
            <Stack
                horizontal
                gap="sm"
                rounded="sm"
                padding="md"
                background="level2"
                alignItems="center"
            >
                <Icon type="info" size="square_sm" color="negative" />
                This space is currently using ETH. You can switch to USDC to use USDC as the
                currency.
            </Stack>
            <Button tone="cta1" onClick={onUpgradeToUsdcClick}>
                Upgrade to USDC
            </Button>
        </Stack>
    )
}

function EditMembershipForm({
    spaceId,
    onChainData,
    onSuccess,
}: {
    spaceId: string | undefined
    onChainData: ReturnType<typeof useLookupOnChainDataForThisForm>
    onSuccess?: () => void
}) {
    const environment = useEnvironment()
    const transactionIsPending = useIsTransactionPending(
        BlockchainTransactionType.EditSpaceMembership,
    )

    const {
        entitlements,
        isEntitlementsLoading,
        pricingModules,
        isLoadingPricingModules,
        minimumMembershipPrice,
        isLoadingMinMembershipPrice,
        roleDetails,
        currentPricingModule,
        isCurrentlyDynamic,
        isLoadingRoleDetails,
        membershipInfo,
        rawPrice,
        isFree,
    } = onChainData

    const defaultValues: EditMembershipSchemaType = useMemo(
        () => ({
            gatingType: entitlements.hasEntitlements ? 'gated' : 'everyone',
            tokensGatedBy: entitlements.tokens,
            ethBalanceGatedBy: entitlements.ethBalance,
            usersGatedBy: entitlements.users,
            membershipLimit: Number(membershipInfo?.maxSupply) ?? 0,
            membershipCost: formatUnits(rawPrice?.toBigInt() ?? 0n),
            membershipPricingType: currentPricingModule?.isFixed ? 'fixed' : 'dynamic',
            clientPricingOption: currentPricingModule?.isFixed
                ? isFree
                    ? 'free'
                    : 'fixed'
                : 'dynamic',
            membershipCurrency: (membershipInfo?.currency as string) ?? zeroAddress,
            prepaidMemberships: 0,
        }),
        [entitlements, membershipInfo, currentPricingModule, isFree, rawPrice],
    )

    if (isEntitlementsLoading) {
        return <ButtonSpinner />
    }

    return (
        <>
            <FormRender schema={editMembershipSchema} defaultValues={defaultValues} mode="all">
                {(hookForm) => {
                    const _form = hookForm as unknown as UseFormReturn<EditMembershipSchemaType>
                    const watchPricingField = _form.watch(['membershipPricingType'])
                    const isFixedPricingSelected = watchPricingField[0] === 'fixed'

                    return (
                        <FormProvider {..._form}>
                            <Stack grow gap paddingY="xs">
                                <Stack gap="paragraph">
                                    <Paragraph strong>Who Can Join</Paragraph>
                                    <EditGating />
                                </Stack>
                                {environment.accountAbstractionConfig && (
                                    <>
                                        <Stack gap="paragraph">
                                            <EditPricingTitle />
                                            {((!currentPricingModule?.isFixed &&
                                                isFixedPricingSelected) ||
                                                (isFree && isFixedPricingSelected)) && (
                                                <AlertBox>
                                                    {!currentPricingModule?.isFixed &&
                                                    isFixedPricingSelected
                                                        ? `This space is using dynamic pricing. If you
                        switch to fixed pricing, you can't go back.`
                                                        : `This town is currently free. If you switch to fixed pricing, you won't be able to change back to free.`}
                                                </AlertBox>
                                            )}
                                            <EditPricing
                                                isFixed={currentPricingModule?.isFixed}
                                                isCurrentlyFree={isFree}
                                                isCurrentlyDynamic={isCurrentlyDynamic}
                                                pricingModules={pricingModules}
                                                isLoadingPricingModules={isLoadingPricingModules}
                                                minimumMembershipPrice={minimumMembershipPrice}
                                                currentMembershipCost={defaultValues.membershipCost}
                                                isLoadingMinMembershipPrice={
                                                    isLoadingMinMembershipPrice
                                                }
                                            />
                                        </Stack>
                                        <Stack gap="paragraph">
                                            <Paragraph strong>Membership</Paragraph>
                                            <EditMembership />
                                        </Stack>
                                    </>
                                )}

                                <EditMembershipSubmitButton
                                    spaceId={spaceId}
                                    transactionIsPending={transactionIsPending}
                                    pricingModules={pricingModules}
                                    minimumMembershipPrice={minimumMembershipPrice}
                                    isLoadingPricingModules={isLoadingPricingModules}
                                    roleDetails={roleDetails}
                                    currentPricingModule={currentPricingModule}
                                    isCurrentlyFree={!!isFree}
                                    isCurrentlyDynamic={!!isCurrentlyDynamic}
                                    isLoadingRoleDetails={isLoadingRoleDetails}
                                    onSuccess={onSuccess}
                                >
                                    Update
                                </EditMembershipSubmitButton>
                            </Stack>
                        </FormProvider>
                    )
                }}
            </FormRender>
        </>
    )
}

const AlertBox = ({ children }: { children: React.ReactNode }) => {
    return (
        <Stack
            horizontal
            background="level2"
            padding="md"
            gap="sm"
            rounded="sm"
            alignItems="center"
        >
            <Icon type="info" color="negative" size="square_sm" />
            <Text size="sm">{children}</Text>
        </Stack>
    )
}

function EditMembershipFormUsdc({
    spaceId,
    onChainData,
    onSuccess,
}: {
    spaceId: string | undefined
    onChainData: ReturnType<typeof useLookupOnChainDataForThisForm>
    onSuccess?: () => void
}) {
    const environment = useEnvironment()
    const transactionIsPending = useIsTransactionPending(
        BlockchainTransactionType.EditSpaceMembership,
    )

    const {
        entitlements,
        isEntitlementsLoading,
        pricingModules,
        isLoadingPricingModules,
        roleDetails,
        isLoadingRoleDetails,
        membershipInfo,
        rawPrice,
        isFree,
    } = onChainData

    const defaultValues: EditMembershipSchemaUsdcType = useMemo(
        () => ({
            gatingType: entitlements.hasEntitlements ? 'gated' : 'everyone',
            tokensGatedBy: entitlements.tokens,
            ethBalanceGatedBy: entitlements.ethBalance,
            usersGatedBy: entitlements.users,
            membershipLimit: Number(membershipInfo?.maxSupply) ?? 0,
            // USDC uses 6 decimals
            membershipCostUsdc: formatUnitsViem(rawPrice?.toBigInt() ?? 0n, 6),
            usdcPricingOption: isFree ? 'free' : 'fixed',
            membershipCurrency: (membershipInfo?.currency as string) ?? zeroAddress,
        }),
        [entitlements, membershipInfo, isFree, rawPrice],
    )

    if (isEntitlementsLoading) {
        return <ButtonSpinner />
    }

    return (
        <>
            <FormRender schema={editMembershipSchemaUsdc} defaultValues={defaultValues} mode="all">
                {(hookForm) => {
                    const _form = hookForm as unknown as UseFormReturn<EditMembershipSchemaUsdcType>

                    return (
                        <FormProvider {..._form}>
                            <Stack grow gap paddingY="xs">
                                <Stack gap="paragraph">
                                    <Paragraph strong>Who Can Join</Paragraph>
                                    <EditGating />
                                </Stack>
                                {environment.accountAbstractionConfig && (
                                    <>
                                        <Stack gap="paragraph">
                                            <EditPricingTitle />
                                            <EditUsdcPricing isCurrentlyFree={!!isFree} />
                                        </Stack>
                                        <Stack gap="paragraph">
                                            <Paragraph strong>Membership</Paragraph>
                                            <EditMembership />
                                        </Stack>
                                    </>
                                )}

                                <EditMembershipSubmitButtonUsdc
                                    spaceId={spaceId}
                                    transactionIsPending={transactionIsPending}
                                    pricingModules={pricingModules}
                                    isLoadingPricingModules={isLoadingPricingModules}
                                    roleDetails={roleDetails}
                                    isLoadingRoleDetails={isLoadingRoleDetails}
                                    onSuccess={onSuccess}
                                >
                                    Update
                                </EditMembershipSubmitButtonUsdc>
                            </Stack>
                        </FormProvider>
                    )
                }}
            </FormRender>
        </>
    )
}

function useLookupOnChainDataForThisForm(args: { spaceId: string | undefined }) {
    const { spaceId } = args
    const {
        data: pricingModules,
        isLoading: isLoadingPricingModules,
        error: pricingModuleError,
    } = usePricingModules()

    const {
        roleDetails,
        isLoading: isLoadingRoleDetails,
        error: roleDetailsError,
    } = useRoleDetails(spaceId ?? '', 1)
    const { data: currentPricingModule, isLoading: isLoadingMembershipPricingModule } =
        useMembershipInfoWithPricingModule(spaceId)
    const {
        data: minimumMembershipPrice,
        isLoading: isLoadingMinMembershipPrice,
        error: minimumMembershipPriceError,
    } = usePlatformMinMembershipPriceInEth()
    const { data: protocolFee, isLoading: isLoadingProtocolFee } = useProtocolFee({ spaceId })

    const { data: _rawPrice, isLoading: isLoadingRawPrice } = useMembershipPrice({
        spaceId,
    })

    const isCurrentlyDynamic = useIsSpaceCurrentlyDynamic({
        spaceId,
    })

    const { data: entitlements, isLoading: isEntitlementsLoading } = useEntitlements(
        spaceId,
        minterRoleId,
    )

    const {
        data: membershipInfo,
        isLoading: isLoadingMembershipInfo,
        error: membershipInfoError,
    } = useMembershipInfo(spaceId ?? '')

    const isLoadingSomeData =
        isLoadingPricingModules ||
        isLoadingRoleDetails ||
        isLoadingMinMembershipPrice ||
        isLoadingMembershipInfo ||
        isLoadingMembershipPricingModule ||
        isEntitlementsLoading ||
        isLoadingRawPrice ||
        isLoadingProtocolFee

    const someErrorOccurred =
        membershipInfoError || roleDetailsError || pricingModuleError || minimumMembershipPriceError

    // when the new omega contracts deploy, the raw price includes the protocol fee.
    // We want to show creators the price of the town without the protocol fee - so less confusing to them, and they can edit the price accurately.
    const rawPrice = _rawPrice?.sub(BigNumber.from(protocolFee ?? 0n))

    const isFree = rawPrice?.eq(0)

    return useMemo(
        () => ({
            pricingModules,
            rawPrice,
            minimumMembershipPrice,
            isFree,
            isCurrentlyDynamic,
            currentPricingModule,
            isLoadingPricingModules,
            isLoadingRoleDetails,
            roleDetails,
            entitlements,
            isEntitlementsLoading,
            isLoadingMinMembershipPrice,
            membershipInfo,
            isLoadingMembershipInfo,
            membershipInfoError,
            isLoadingSomeData,
            someErrorOccurred,
            protocolFee,
            isLoadingProtocolFee,
        }),
        [
            pricingModules,
            rawPrice,
            minimumMembershipPrice,
            isFree,
            isCurrentlyDynamic,
            currentPricingModule,
            isLoadingPricingModules,
            isLoadingRoleDetails,
            roleDetails,
            entitlements,
            isEntitlementsLoading,
            isLoadingMinMembershipPrice,
            membershipInfo,
            isLoadingMembershipInfo,
            membershipInfoError,
            isLoadingSomeData,
            someErrorOccurred,
            protocolFee,
            isLoadingProtocolFee,
        ],
    )
}
